import React, { useEffect, useState, useRef } from "react";

import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { Box, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import {
  CreateImmunization,
  UpdateImmunizationById,
  getImmunizationById,
} from "../../Api/Services/ImmunizationServices";
import { getPetNameByWorkspaceId } from "../../Api/Services/PetServices";
import AutoComplete from "../../Components/Common/AutoComplete";
import CustomButton from "../../Components/Common/Button";
import FormGroup from "../../Components/Common/FormGroup";
import FormLayout from "../../Components/Common/FormHeader";
import InputField from "../../Components/Common/InputField";
import  { showSnackbar } from "../../Components/Common/Snackbar";
import TextArea from "../../Components/Common/TextArea";
import "./immunization.css";
import { WORKSPACEID_KEY } from "../../Constant";
import {
  FormHeaderBox,
  FormHeaderTitle,
  Body,
  ButtonBoxStyle,
  Column,
  CancelButton,
} from "../../Css";
import { ImmunizationFormValidation } from "../../From/Validation";
import { VaccinationType } from "../../Options";
import { formatDate } from "../../Utils";
import "../../Components/pet-information/petPrint.css";
import { useTheme } from "@mui/material/styles";

const defaultValue = {
  vaccination_type: "",
  date: "",
  information: "",
  pet_id: "",
};

const ImmunizationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const componentPDF = useRef();

  // Determine if this is edit mode
  const isEditMode = !!id;

  const [initialValue, setInitialValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const w_id = localStorage.getItem(WORKSPACEID_KEY);

  // Load existing data for edit mode
  const loadImmunizationDetails = async () => {
    if (!isEditMode) return;
    
    try {
      const { data } = await getImmunizationById(id);
      setInitialValue({
        ...initialValue,
        vaccination_type: data?.data?.[0]?.vaccination_type,
        date: formatDate(data?.data?.[0]?.date),
        information: data?.data?.[0]?.information,
        pet_id: data?.data?.[0]?.pet_id,
      });
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to load immunization details.",
      });
    }
  };

  const getPetNames = async () => {
    try {
      const { data } = await getPetNameByWorkspaceId(w_id);
      const filteredData = data?.data?.map((item) => {
        return {
          value: item?.id,
          label: item?.name,
        };
      });
      setItems(filteredData);
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to fetch pet names",
      });
    }
  };

  useEffect(() => {
    getPetNames();
    if (isEditMode) {
      loadImmunizationDetails();
    }
  }, [w_id, isEditMode]);

  // Create new immunization
  const createImmunizationData = async (values) => {
    const value = {
      vaccination_type: values?.vaccination_type,
      date: values?.date,
      information: values?.information,
      pet_id: values?.pet_id,
    };
    
    try {
      await CreateImmunization(value);
      showSnackbar({
        type: "success",
        message: "Immunization added successfully.",
      });
    } catch (err) {
      showSnackbar({
        type: "error",
        message: "Immunization add failed.",
      });
      throw err;
    }
  };

  // Update existing immunization
  const updateImmunizationData = async (values) => {
    const value = {
      vaccination_type: values?.vaccination_type,
      date: values?.date,
      information: values?.information,
      pet_id: values?.pet_id,
    };
    
    try {
      await UpdateImmunizationById(id, value);
      showSnackbar({
        type: "success",
        message: "Immunization updated successfully.",
      });
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Immunization update failed.",
      });
      throw error;
    }
  };

  const handleSubmit = async (values, { isSubmitting }) => {
    try {
      if (isEditMode) {
        await updateImmunizationData(values);
      } else {
        await createImmunizationData(values);
      }
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to process immunization:",
      });
    } finally {
      isSubmitting(false);
    }
  };

  // Handle print functionality
  const handlePrint = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "Immunization Information",
    pageStyle: `
      @page { size: A3; margin: 200px; }
      body { margin: 100px; }
      .print-text-field { font-size: 14px; }
    `,
  });

  return (
    <FormLayout title={isEditMode ? "Edit Immunization Information" : "Immunization Information"}>
      <Formik
        initialValues={initialValue}
        onSubmit={handleSubmit}
        validationSchema={ImmunizationFormValidation}
        enableReinitialize
      >
        {({ getFieldProps, values, isSubmitting }) => (
          <Form>
            <Box sx={FormHeaderBox(theme.palette.primary.main)}>
              <Box sx={{ borderBottom: `1px solid ${theme.palette.primary.main}`, width: "100%" }}>
                <Typography variant="h6" sx={FormHeaderTitle}>
                  Immunization Details
                </Typography>
              </Box>
              <div className="scroll2" ref={componentPDF} style={{ width: "100%"}}>
                <Body style={{ gap: "10px 20px" }}>
                  <Column>
                    <FormGroup name="pet_id" label={"Pet Name"} requiredField>
                      <AutoComplete
                        {...getFieldProps("pet_id")}
                        options={items}
                        value={isEditMode ? items.find(option => option.value === values.pet_id) : undefined}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                        <FormGroup
                          name="vaccination_type"
                          label={"Vaccination Type"}
                          requiredField
                        >
                          <AutoComplete
                            {...getFieldProps("vaccination_type")}
                            options={VaccinationType}
                            otherType="Other"
                            placeholder="Select a type"
                            value={isEditMode ? VaccinationType.find(option => option.value === values.vaccination_type) : undefined}
                          />
                        </FormGroup>
                    </Column>
                    <Column>
                        <FormGroup name="date" label={"Date"} requiredField>
                          <InputField type="date" {...getFieldProps("date")} />
                        </FormGroup>
                    </Column>
                  </Body>
                  <Body style={{ gap: "10px 20px", marginTop: "10px" }}>
                  <Column>
                    <FormGroup name="information" label={"Information"}>
                      <TextArea
                        maxLength={300}
                        rows={10}
                        {...getFieldProps("information")}
                      />
                    </FormGroup>
                    </Column>
                    </Body>
                  </div>
              <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                <Box display="flex">
                  <CustomButton
                    label="Cancel"
                    type="button"
                    style={CancelButton(`${theme.palette.primary.main}`)}
                    onClick={() => navigate(-1)}
                  />
                  <CustomButton
                    label={isSubmitting ? (isEditMode ? "Saving..." : "Submitting...") : (isEditMode ? "Save" : "Submit")}
                    disabled={isSubmitting}
                    type="submit"
                  />
                  {isEditMode && (
                    <CustomButton
                      startIcon={<LocalPrintshopOutlinedIcon />}
                      onClick={handlePrint}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </FormLayout>
  );
};

export default ImmunizationForm;
